import { FontMetadata } from "../types/customFontTypes";

export const MAX_FONT_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB matching iOS

export interface FontValidationResult {
  valid: boolean;
  error?: string;
  metadata?: FontMetadata;
}

/**
 * Validates a font file and extracts its metadata (family, full name, PostScript name)
 * using client-side binary parsing of OpenType / TrueType sfnt table structures.
 */
export async function parseAndValidateFont(
  fileOrBlob: File | Blob,
  fallbackFileName = "font.ttf"
): Promise<FontValidationResult> {
  const fileName = fileOrBlob instanceof File ? fileOrBlob.name : fallbackFileName;
  const extension = fileName.split(".").pop()?.toLowerCase() || "";
  if (extension !== "ttf") {
    return {
      valid: false,
      error: "Only TTF (.ttf) font files are supported.",
    };
  }

  if (fileOrBlob.size <= 0) {
    return {
      valid: false,
      error: "Font file is empty.",
    };
  }

  if (fileOrBlob.size > MAX_FONT_FILE_SIZE_BYTES) {
    return {
      valid: false,
      error: "Font file size must be less than 10MB.",
    };
  }

  try {
    const arrayBuffer = await fileOrBlob.arrayBuffer();
    const metadata = parseFontMetadataFromBuffer(arrayBuffer, fileName);
    if (!metadata) {
      return {
        valid: false,
        error: "The selected file is not a valid font or font metadata could not be read.",
      };
    }

    return {
      valid: true,
      metadata,
    };
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : "Failed to read font file";
    return {
      valid: false,
      error: message,
    };
  }
}

/**
 * Parses OpenType/TrueType binary table directory and 'name' table.
 */
export function parseFontMetadataFromBuffer(
  buffer: ArrayBuffer,
  fallbackFileName: string
): FontMetadata | null {
  if (!buffer || buffer.byteLength < 12) return null;

  const dataView = new DataView(buffer);
  
  // Verify sfnt magic: 0x00010000 (TrueType), 'true' (TrueType), 'OTTO' (OpenType/CFF), or 'typ1'
  const sfntVersion = dataView.getUint32(0);
  const isTrueType = sfntVersion === 0x00010000 || sfntVersion === 0x74727565; // 'true'
  const isOpenType = sfntVersion === 0x4f54544f; // 'OTTO'
  const isWoff = sfntVersion === 0x774f4646; // 'wOFF'
  
  if (!isTrueType && !isOpenType && !isWoff) {
    // If not standard sfnt header, fallback to sanitized file name if at least large enough
    const baseName = fallbackFileName.replace(/\.ttf$/i, "").trim();
    return {
      fontFamily: baseName,
      fullName: baseName,
      postScriptName: baseName.replace(/[\s_]+/g, "-"),
    };
  }

  const numTables = dataView.getUint16(4);
  let nameTableOffset = 0;

  for (let i = 0; i < numTables; i++) {
    const offset = 12 + i * 16;
    if (offset + 16 > buffer.byteLength) break;
    
    let tag = "";
    for (let j = 0; j < 4; j++) {
      tag += String.fromCharCode(dataView.getUint8(offset + j));
    }
    
    if (tag === "name") {
      nameTableOffset = dataView.getUint32(offset + 8);
      break;
    }
  }

  const cleanFallbackName = fallbackFileName.replace(/\.ttf$/i, "").trim();

  if (!nameTableOffset || nameTableOffset + 6 > buffer.byteLength) {
    return {
      fontFamily: cleanFallbackName,
      fullName: cleanFallbackName,
      postScriptName: cleanFallbackName.replace(/[\s_]+/g, "-"),
    };
  }

  try {
    const count = dataView.getUint16(nameTableOffset + 2);
    const stringStorageOffset = nameTableOffset + dataView.getUint16(nameTableOffset + 4);
    const names: Record<number, string> = {};

    for (let i = 0; i < count; i++) {
      const recordOffset = nameTableOffset + 6 + i * 12;
      if (recordOffset + 12 > buffer.byteLength) break;

      const platformID = dataView.getUint16(recordOffset);
      const encodingID = dataView.getUint16(recordOffset + 2);
      const nameID = dataView.getUint16(recordOffset + 6);
      const length = dataView.getUint16(recordOffset + 8);
      const strOffset = stringStorageOffset + dataView.getUint16(recordOffset + 10);

      if (strOffset + length > buffer.byteLength) continue;

      let str = "";
      if (platformID === 0 || (platformID === 3 && (encodingID === 1 || encodingID === 10))) {
        // UTF-16BE encoding
        for (let j = 0; j < length; j += 2) {
          str += String.fromCharCode(dataView.getUint16(strOffset + j));
        }
      } else {
        // Latin-1 / ASCII encoding
        for (let j = 0; j < length; j++) {
          str += String.fromCharCode(dataView.getUint8(strOffset + j));
        }
      }

      str = str.replace(/\0/g, "").trim();
      if (!str) continue;

      // Prefer Windows (platform 3) or Unicode (platform 0)
      if (!names[nameID] || platformID === 3 || platformID === 0) {
        names[nameID] = str;
      }
    }

    const fontFamily = names[1] || names[4] || names[6] || cleanFallbackName;
    const subFamily = names[2] || "";
    const fullName = names[4] || names[1] || names[6] || cleanFallbackName;
    // PostScript names must not contain spaces or special characters
    const rawPostScript = names[6] || names[1] || names[4] || cleanFallbackName;
    const postScriptName = rawPostScript.replace(/[\s_]+/g, "-").replace(/[^a-zA-Z0-9-]/g, "");

    return {
      fontFamily: fontFamily.trim(),
      subFamily: subFamily.trim(),
      fullName: fullName.trim(),
      postScriptName: postScriptName.trim() || cleanFallbackName.replace(/[\s_]+/g, "-"),
    };
  } catch (e) {
    return {
      fontFamily: cleanFallbackName,
      fullName: cleanFallbackName,
      postScriptName: cleanFallbackName.replace(/[\s_]+/g, "-"),
    };
  }
}
